import type { TemplateNode, TemplateWhere } from '@/uptk/core/components/moduleEntity/template/templateTypes';
import type { DesignModuleSpecJson } from '../data/contracts';
import { entitiesOf } from './templateBindings';

const displays = ['name', 'label', 'title', 'titre', 'libelle', 'enonce', 'pseudo', 'date'];
/** Only declared, unambiguous child relations. No row values or prototype code are inspected. */
export function relatedCollection(spec: DesignModuleSpecJson, owner: string, scope: 'current' | 'item', node: TemplateNode): TemplateNode | null {
  if (node.type !== 'el' || (node.candidate?.kind !== 'repeat' && !['ul', 'ol'].includes(node.tag) && !(node.attrs?.role === 'group' && node.children.length >= 2 && node.children.every(child => child.type === 'el' && child.tag === 'button')))) return null;
  const rows = node.children.filter((n): n is Extract<TemplateNode, { type: 'el' }> => n.type === 'el');
  if (rows.length < 2 || !rows.every(row => row.tag === rows[0].tag && (['ul', 'ol'].includes(node.tag) || rows[0].tag === 'button' || JSON.stringify(row.cls ?? []) === JSON.stringify(rows[0].cls ?? [])))) return null;
  // An option list or a collection of expandable records is structural evidence, unlike metric cards.
  if (!(['ul', 'ol'].includes(node.tag) || rows.every(row => ['button', 'details', 'tr'].includes(row.tag)))) return null;
  const entities = entitiesOf(spec);
  const choices = Object.entries(entities).flatMap(([code, entity]) => {
    const refs = entity.fields.filter(field => field.data?.type === 'store' && field.data.entity === owner && ['typeahead', 'entityref'].includes(field.type));
    if (refs.length !== 1) return [];
    let display = displays.find(name => entity.fields.some(field => field.name === name));
    if (!display && rows[0].tag === 'details') {
      const targets = entity.fields.flatMap(field => {
        if (field.data?.type !== 'store' || field.data.entity === owner) return [];
        const target = entities[field.data.entity ?? ''];
        const name = displays.find(name => target?.fields.some(f => f.name === name));
        return name ? [[field.name, name].join('.')] : [];
      });
      if (targets.length === 1) display = targets[0];
      else { const statements = targets.filter(path => path.endsWith('.enonce')); if (statements.length === 1) display = statements[0]; }
    }
    if (!display) return [];
    const ref = refs[0];
    const where: TemplateWhere = { field: ref.name, equals: { scope, entity: owner, ...(ref.data?.valueField && ref.data.valueField !== 'id' ? { field: ref.data.valueField } : {}) } };
    return [{ code, display, where }];
  });
  if (choices.length !== 1) return null;
  const choice = choices[0];
  // Replacing all row content prevents stale option letters, correctness ticks and demo answers from surviving.
  const item: TemplateNode = { ...rows[0], cls: (rows[0].cls ?? []).filter(cls => rows.every(row => row.cls?.includes(cls))), candidate: undefined, children: [{ type: 'field', entity: choice.code, field: choice.display, scope: 'item' }] };
  return { ...node, candidate: undefined, children: [{ type: 'repeat', source: { entity: choice.code, where: choice.where }, item, empty: { type: 'text', token: '—' } }] };
}

export function nestedCollections(spec: DesignModuleSpecJson, owner: string, node: TemplateNode): TemplateNode {
  const proposed = relatedCollection(spec, owner, 'item', node);
  if (proposed) return proposed;
  return node.type === 'el' ? { ...node, children: node.children.map(child => nestedCollections(spec, owner, child)) } : node;
}
